import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CloseIcon from '@material-ui/icons/Close'
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tabs,
  Tab,
  Button,
  Dropzone,
  IconButton,
  ListItem,
  ListItemText,
  SearchBar,
  Spinner,
  FilePreview,
  useDebounce,
  makeStyles,
  imageCrossOrigin,
  retryCorsImageOnError
} from '@paintscout/ui'
import type { DialogProps, MuiTheme as Theme } from '@paintscout/ui'
import type { WithClientOptions, WithUser } from '@paintscout/ui-business'
import { withClientOptions, withUser } from '@paintscout/ui-business'
import { hasIntegrationInterface } from '@paintscout/util/builder'
import { removeAuth0Prefix } from '@paintscout/util/users'
import type { CompanyCamProject } from './AddFileDialog.helpers'
import { orderPickedPhotos, readLastCcProject, writeLastCcProject } from './AddFileDialog.helpers'
import { useCompanyQuery, useContactQuery, useDealQuery } from '@paintscout/api'
import type { Company, Contact, Deal } from '@paintscout/api'
import type { QuoteFile, QuoteFileVisibility } from 'paintscout'
import { useMutation } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'

export type AddFileDialogTab = 'upload' | 'deal-files' | 'company-cam'

export interface AddFileDialogProps extends Omit<DialogProps, 'onClose' | 'title'> {
  /** Title shown at the top. Defaults to 'Add File'. */
  title?: string
  /** Optional subtitle under the title (e.g. 'Filter Primary'). */
  subtitle?: string

  /** Max file size for upload, in bytes. Defaults to 100MB. */
  maxFileSize?: number
  /** Accepted MIME types for upload. Defaults to images + PDF. */
  accept?: string

  /** Optional doc whose attached files populate the 'Deal Files' tab. */
  fileDocId?: string
  fileDocType?: 'contact' | 'company' | 'deal'
  /**
   * Pre-fetched files to populate the source tab directly, instead of querying a
   * single doc via fileDocId/fileDocType. Used to pull from linked estimates into
   * a deal (the reverse of the deal -> estimate flow). When provided, takes
   * precedence over the queried doc files.
   */
  sourceFiles?: QuoteFile[]
  /** Override the 'Deal Files' tab label (defaults to 'Deal Files'). */
  dealFilesLabel?: string

  /** Tab to open initially. Defaults to 'upload'. */
  initialTab?: AddFileDialogTab

  /** Called with raw File objects when the user uploads via the Upload tab. */
  onUpload?: (files: File[]) => void

  /**
   * Called with existing QuoteFiles when the user picks from a source tab
   * (Deal Files or Company Cam). Company Cam files carry `provider: 'companycam'`
   * so the editor can disable in-app image editing for live remote URLs.
   */
  onSelectFiles?: (files: QuoteFile[]) => void

  /** Close handler — fires on X click, Cancel button, or backdrop dismissal. */
  onCancel?: () => void
}

const PROCESS_EVENT = gql`
  mutation ($type: String!, $provider: String!, $async: Boolean, $params: JSON) {
    processEvent(type: $type, provider: $provider, async: $async, params: $params) {
      provider
      result
      error
    }
  }
`

type CompanyCamPhotoFile = QuoteFile & { _photoId: string }

type AddFileDialogPropsWithInjections = AddFileDialogProps & WithClientOptions & WithUser

const DEFAULT_MAX_FILE_SIZE = 1024 * 1024 * 100 // 100MB
const DEFAULT_ACCEPT = 'image/*,application/pdf'

const useStyles = makeStyles<Theme>((theme) => ({
  paper: {
    height: 'calc(100vh - 96px)',
    maxHeight: 720,
    width: '100%',
    maxWidth: 720,
    [theme.breakpoints.down('sm')]: {
      height: '100%',
      maxHeight: '100%',
      maxWidth: '100%'
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  },
  titleStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25)
  },
  titleSubtitle: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P50
  },
  content: {
    padding: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  tabsRow: {
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  tabLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    textTransform: 'none'
  },
  availabilityDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: theme.palette.success?.main ?? '#22c55e',
    display: 'inline-block'
  },
  panel: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column'
  },
  dropzoneRoot: {
    flex: 1,
    minHeight: 240,
    border: `1.5px dashed ${theme.paintscout.palette.brand.Neutral.P85}`,
    borderRadius: theme.borderRadius.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.paintscout.palette.brand.Neutral.P98 ?? theme.palette.common.white,
    color: theme.paintscout.palette.brand.Neutral.P50,
    cursor: 'pointer',
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '&:hover': {
      borderColor: theme.palette.primary.main
    }
  },
  dropzonePreview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  dropzoneIcon: {
    fontSize: 36,
    color: theme.paintscout.palette.brand.Neutral.P50
  },
  dropzoneText: {
    ...theme.typography.body2,
    color: theme.paintscout.palette.brand.Neutral.P30
  },
  fileMeta: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P50,
    marginTop: theme.spacing(1)
  },
  filesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: theme.spacing(1.5),
    flex: 1,
    alignContent: 'start'
  },
  fileTile: {
    position: 'relative',
    // padding-top = width keeps tiles square; aspect-ratio is unreliable when
    // the percentage-height image feeds back into auto-sized grid rows
    height: 0,
    padding: '100% 0 0',
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    cursor: 'pointer',
    background: theme.paintscout.palette.brand.Neutral.P96,
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      borderColor: theme.paintscout.palette.brand.Neutral.P70
    }
  },
  fileTileSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  },
  fileTileImg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  tileFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.paintscout.palette.brand.Neutral.P50,
    textAlign: 'center',
    padding: theme.spacing(4)
  },
  ccPanel: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2)
  },
  ccSearch: {
    marginBottom: theme.spacing(1)
  },
  ccProjectList: {
    flex: 1,
    overflow: 'auto'
  },
  ccProjectThumb: {
    width: 56,
    height: 56,
    objectFit: 'cover',
    borderRadius: theme.borderRadius.md,
    marginRight: theme.spacing(2),
    background: theme.paintscout.palette.brand.Neutral.P96
  },
  ccProjectHeader: {
    marginBottom: theme.spacing(1)
  },
  ccProjectHeaderName: {
    ...theme.typography.body2,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.paintscout.palette.brand.Neutral.P30
  },
  ccProjectHeaderAddress: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P50
  },
  ccSubTabsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1)
  },
  ccPhotoGrid: {
    flex: 1,
    overflow: 'auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: theme.spacing(1.5),
    alignContent: 'start',
    // small padding so the selected tile's box-shadow ring isn't clipped at the scroll edges
    padding: theme.spacing(0.5)
  },
  ccPhotoTile: {
    position: 'relative',
    height: 0,
    padding: '100% 0 0',
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    cursor: 'pointer',
    background: theme.paintscout.palette.brand.Neutral.P96,
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      borderColor: theme.paintscout.palette.brand.Neutral.P70
    }
  },
  ccPhotoTileSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}`
  },
  ccPhotoImg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  // Caption overlay on a picker tile — mirrors the FilesGridNext overlay so the
  // CompanyCam description is visible while selecting. The gradient + padding
  // live on this container; the clamp lives on the inner text span. Keeping the
  // padding OFF the -webkit-box element avoids the iOS Safari bug where bottom
  // padding makes the last clamped line render cut off mid-glyph (#21231).
  ccPhotoCaption: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: theme.spacing(0.5, 0.75),
    color: theme.palette.common.white,
    background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0) 100%)',
    // let clicks fall through to the tile button beneath
    pointerEvents: 'none'
  },
  ccPhotoCaptionText: {
    ...theme.typography.caption,
    lineHeight: 1.25,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  ccToolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  }
}))

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${Math.round(bytes / (1024 * 1024 * 1024))}GB`
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))}MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}KB`
  return `${bytes}B`
}

function AddFileDialog(props: AddFileDialogPropsWithInjections) {
  const {
    title = 'Add File',
    subtitle,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    accept = DEFAULT_ACCEPT,
    fileDocId,
    fileDocType,
    sourceFiles,
    dealFilesLabel = 'Deal Files',
    initialTab,
    onUpload,
    onSelectFiles,
    onCancel,
    clientOptions,
    user,
    ...dialogProps
  } = props

  const classes = useStyles({})
  const ccUserId = removeAuth0Prefix(user?.user?.user_id ?? '')

  const companyCamAvailable = useMemo(
    () => hasIntegrationInterface({ options: clientOptions.options, integration: 'companycam' }),
    [clientOptions.options]
  )
  const hasPrefetchedSource = !!sourceFiles
  const dealFilesAvailable = hasPrefetchedSource ? sourceFiles.length > 0 : !!fileDocId && !!fileDocType

  const initial: AddFileDialogTab = initialTab ?? 'upload'
  const [currentTab, setCurrentTab] = useState<AddFileDialogTab>(initial)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set())

  const { data: contactData } = useContactQuery({
    variables: { id: fileDocId },
    skip: hasPrefetchedSource || !dealFilesAvailable || fileDocType !== 'contact',
    fetchPolicy: 'cache-and-network'
  })
  const { data: companyData } = useCompanyQuery({
    variables: { id: fileDocId },
    skip: hasPrefetchedSource || !dealFilesAvailable || fileDocType !== 'company',
    fetchPolicy: 'cache-and-network'
  })
  const { data: dealData } = useDealQuery({
    variables: { id: fileDocId },
    skip: hasPrefetchedSource || !dealFilesAvailable || fileDocType !== 'deal',
    fetchPolicy: 'cache-and-network'
  })

  const sourceDoc: Contact | Company | Deal | null =
    (contactData?.contact as Contact) || (companyData?.company as Company) || (dealData?.deal as Deal) || null
  const docFiles: QuoteFile[] = useMemo(
    () => sourceFiles ?? (sourceDoc ? (Object.values(sourceDoc?.files ?? {}) as QuoteFile[]) : []),
    [sourceFiles, sourceDoc]
  )

  // If the initially-selected tab becomes unavailable as props change, fall back to Upload.
  useEffect(() => {
    if (currentTab === 'deal-files' && !dealFilesAvailable) setCurrentTab('upload')
    if (currentTab === 'company-cam' && !companyCamAvailable) setCurrentTab('upload')
  }, [currentTab, dealFilesAvailable, companyCamAvailable])

  // --- Company Cam picker state ---
  const [ccQuery, setCcQuery] = useState('')
  const ccDebouncedQuery = useDebounce(ccQuery, 250)
  const [ccProjects, setCcProjects] = useState<CompanyCamProject[]>([])
  const [ccSelectedProject, setCcSelectedProject] = useState<CompanyCamProject | null>(null)
  const [ccLoadingProjects, setCcLoadingProjects] = useState(false)
  const [ccLoadingFiles, setCcLoadingFiles] = useState(false)
  const [ccOriginalPhotos, setCcOriginalPhotos] = useState<CompanyCamPhotoFile[]>([])
  const [ccAnnotatedPhotos, setCcAnnotatedPhotos] = useState<CompanyCamPhotoFile[]>([])
  const [ccPhotoTab, setCcPhotoTab] = useState<'annotated' | 'original'>('annotated')
  const [ccSelectedPhotoKeys, setCcSelectedPhotoKeys] = useState<Set<string>>(() => new Set())
  const [ccPage, setCcPage] = useState(1)
  const [ccHasMore, setCcHasMore] = useState(false)
  const [ccCaptionedOnly, setCcCaptionedOnly] = useState(false)
  // Guards the one-shot auto-restore so backing out of a remembered project
  // (handleCcBack) lets the user browse instead of snapping straight back.
  const ccRestoredRef = useRef(false)

  const [processEvent] = useMutation<any>(PROCESS_EVENT)

  const searchCcProjects = useCallback(
    async (query: string) => {
      if (currentTab !== 'company-cam' || ccSelectedProject) return
      setCcLoadingProjects(true)
      try {
        const { data } = await processEvent({
          variables: { type: 'get-projects', provider: 'companycam', async: false, params: { query } }
        })
        setCcProjects((data?.processEvent?.result?.result ?? []) as CompanyCamProject[])
      } finally {
        setCcLoadingProjects(false)
      }
    },
    [currentTab, ccSelectedProject, processEvent]
  )

  const fetchCcFiles = useCallback(
    async (projectId: string, page: number) => {
      if (page === 1) setCcLoadingFiles(true)
      try {
        const { data } = await processEvent({
          variables: { type: 'get-files', provider: 'companycam', async: false, params: { projectId, page } }
        })
        const results = (data?.processEvent?.result?.result ?? []) as Array<{
          id: string
          url?: string
          annotation_url?: string
          description?: string
        }>
        setCcHasMore(results.length === 100)
        const visibility: QuoteFileVisibility = 'visible'
        const newOriginals: CompanyCamPhotoFile[] = results.map((photo) => ({
          _photoId: photo.id,
          key: `${photo.id}-original`,
          visibility,
          type: 'image',
          src: photo.url,
          title: photo?.description ?? '',
          caption: photo?.description ?? '',
          provider: 'companycam'
        }))
        const newAnnotated: CompanyCamPhotoFile[] = results.map((photo) => ({
          _photoId: photo.id,
          key: `${photo.id}-annotated`,
          visibility,
          type: 'image',
          src: photo.annotation_url ?? photo.url,
          title: photo?.description ?? '',
          caption: photo?.description ?? '',
          provider: 'companycam'
        }))
        if (page === 1) {
          setCcOriginalPhotos(newOriginals)
          setCcAnnotatedPhotos(newAnnotated)
        } else {
          setCcOriginalPhotos((prev) => [...prev, ...newOriginals])
          setCcAnnotatedPhotos((prev) => [...prev, ...newAnnotated])
        }
      } finally {
        if (page === 1) setCcLoadingFiles(false)
      }
    },
    [processEvent]
  )

  // Select a project and remember it for next time the picker opens.
  const selectCcProject = useCallback(
    (project: CompanyCamProject) => {
      setCcSelectedProject(project)
      writeLastCcProject(ccUserId, project)
    },
    [ccUserId]
  )

  // On first entering the CompanyCam tab, jump straight to the last-used project
  // (if any). The ref is only tripped once a restore actually happens, so a tab
  // visit with nothing saved doesn't permanently block a later restore, and
  // handleCcBack still lets the user browse.
  useEffect(() => {
    if (currentTab !== 'company-cam' || ccRestoredRef.current || ccSelectedProject) return
    const last = readLastCcProject(ccUserId)
    if (last) {
      ccRestoredRef.current = true
      setCcSelectedProject(last)
    }
  }, [currentTab, ccUserId, ccSelectedProject])

  // Fetch projects when entering the tab or when search changes.
  useEffect(() => {
    if (currentTab === 'company-cam' && !ccSelectedProject) {
      searchCcProjects(ccDebouncedQuery)
    }
  }, [currentTab, ccSelectedProject, ccDebouncedQuery, searchCcProjects])

  // Fetch files when a project is picked.
  useEffect(() => {
    if (ccSelectedProject) {
      setCcPage(1)
      fetchCcFiles(ccSelectedProject.id, 1)
    }
  }, [ccSelectedProject, fetchCcFiles])

  const handleCcBack = () => {
    setCcSelectedProject(null)
    setCcOriginalPhotos([])
    setCcAnnotatedPhotos([])
    setCcSelectedPhotoKeys(new Set())
    setCcPage(1)
    setCcHasMore(false)
    setCcPhotoTab('annotated')
  }

  const handleCcTogglePhoto = (file: CompanyCamPhotoFile) => {
    setCcSelectedPhotoKeys((prev) => {
      const next = new Set(prev)
      if (next.has(file.key)) next.delete(file.key)
      else next.add(file.key)
      return next
    })
  }

  const handleCcSelectAllVisible = () => {
    const base = ccPhotoTab === 'annotated' ? ccAnnotatedPhotos : ccOriginalPhotos
    const visible = ccCaptionedOnly ? base.filter((f) => !!f.caption) : base
    const everySelected = visible.length > 0 && visible.every((f) => ccSelectedPhotoKeys.has(f.key))
    setCcSelectedPhotoKeys((prev) => {
      const next = new Set(prev)
      if (everySelected) visible.forEach((f) => next.delete(f.key))
      else visible.forEach((f) => next.add(f.key))
      return next
    })
  }

  const handleCcLoadMore = async () => {
    if (!ccSelectedProject || !ccHasMore) return
    const nextPage = ccPage + 1
    setCcPage(nextPage)
    await fetchCcFiles(ccSelectedProject.id, nextPage)
  }

  const handleCcConfirm = () => {
    if (!onSelectFiles) return
    // ccSelectedPhotoKeys is a Set (insertion-ordered) so picked files arrive in
    // the order the user clicked them rather than fixed grid order.
    const picked = orderPickedPhotos(ccSelectedPhotoKeys, [...ccAnnotatedPhotos, ...ccOriginalPhotos])
    if (!picked.length) return
    onSelectFiles(picked.map(({ _photoId: _id, ...file }) => file))
    setCcSelectedPhotoKeys(new Set())
  }

  const handleUploadDrop = (files: File[]) => {
    if (!files.length || !onUpload) return
    onUpload(files)
  }

  const handleToggleSourceFile = (file: QuoteFile) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(file.key)) next.delete(file.key)
      else next.add(file.key)
      return next
    })
  }

  const handleConfirmSelection = () => {
    if (!onSelectFiles) return
    const picked = docFiles.filter((f) => selectedKeys.has(f.key))
    if (!picked.length) return
    onSelectFiles(picked)
    setSelectedKeys(new Set())
  }

  const renderTabLabel = (label: string) => (
    <span className={classes.tabLabel}>
      <span className={classes.availabilityDot} aria-hidden />
      {label}
    </span>
  )

  const renderUploadPanel = () => (
    <div className={classes.panel}>
      <Dropzone
        accept={accept}
        maxSize={maxFileSize}
        multiple
        onDrop={handleUploadDrop}
        className={classes.dropzoneRoot}
        shouldRenderPreview
        renderPreview={() => (
          <div className={classes.dropzonePreview}>
            <CloudUploadOutlinedIcon className={classes.dropzoneIcon} />
            <div className={classes.dropzoneText}>Select or drag and drop</div>
          </div>
        )}
      />
      <div className={classes.fileMeta}>Max file size {formatFileSize(maxFileSize)}</div>
    </div>
  )

  const renderDealFilesPanel = () => {
    if (!docFiles.length) {
      return (
        <div className={classes.panel}>
          <div className={classes.emptyState}>
            {hasPrefetchedSource
              ? 'No files to pull yet.'
              : `No files attached to this ${fileDocType ?? 'record'} yet.`}
          </div>
        </div>
      )
    }
    return (
      <div className={classes.panel}>
        <div className={classes.filesGrid}>
          {docFiles.map((file) => {
            const isImage = file.type?.startsWith('image') || file.format?.startsWith('image')
            const isSelected = selectedKeys.has(file.key)
            return (
              <button
                key={file.key}
                type="button"
                className={`${classes.fileTile} ${isSelected ? classes.fileTileSelected : ''}`}
                onClick={() => handleToggleSourceFile(file)}
              >
                {isImage && file.src ? (
                  // Match the new file editor's CORS load so Safari keeps a
                  // single CORS-clean cache entry for this src.
                  <img
                    crossOrigin={imageCrossOrigin(file)}
                    src={file.src}
                    alt={file.title || ''}
                    className={classes.fileTileImg}
                    onError={retryCorsImageOnError}
                  />
                ) : (
                  <span className={classes.tileFill}>{file.title || 'File'}</span>
                )}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  const renderCompanyCamPanel = () => {
    if (!ccSelectedProject) {
      return (
        <div className={classes.ccPanel}>
          <div className={classes.ccSearch}>
            <SearchBar placeholder="Search Projects..." onChange={setCcQuery} value={ccQuery} />
          </div>
          <div className={classes.ccProjectList}>
            {ccLoadingProjects ? (
              <Spinner padded />
            ) : ccProjects.length === 0 ? (
              <div className={classes.emptyState}>No Company Cam projects found.</div>
            ) : (
              ccProjects.map((project) => (
                <ListItem key={project.id} divider button onClick={() => selectCcProject(project)}>
                  {project.featuredImage && (
                    <img src={project.featuredImage} alt="" className={classes.ccProjectThumb} />
                  )}
                  <ListItemText
                    primary={project.name ?? project.id}
                    secondary={project.address?.street_address_1 ?? ''}
                  />
                </ListItem>
              ))
            )}
          </div>
        </div>
      )
    }

    const visiblePhotos = ccPhotoTab === 'annotated' ? ccAnnotatedPhotos : ccOriginalPhotos
    const displayedPhotos = ccCaptionedOnly ? visiblePhotos.filter((f) => !!f.caption) : visiblePhotos
    const visibleAllSelected =
      displayedPhotos.length > 0 && displayedPhotos.every((f) => ccSelectedPhotoKeys.has(f.key))

    return (
      <div className={classes.ccPanel}>
        <div className={classes.ccProjectHeader}>
          <div className={classes.ccProjectHeaderName}>{ccSelectedProject.name ?? ccSelectedProject.id}</div>
          {ccSelectedProject.address?.street_address_1 && (
            <div className={classes.ccProjectHeaderAddress}>{ccSelectedProject.address.street_address_1}</div>
          )}
        </div>
        <div className={classes.ccSubTabsRow}>
          <Tabs
            value={ccPhotoTab}
            onChange={(_, value) => setCcPhotoTab(value as 'annotated' | 'original')}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab value="annotated" label="Annotated" />
            <Tab value="original" label="Original" />
          </Tabs>
          <div className={classes.ccToolbarActions}>
            <Button
              variant={ccCaptionedOnly ? 'contained' : 'outlined'}
              color={ccCaptionedOnly ? 'primary' : 'default'}
              size="small"
              onClick={() => setCcCaptionedOnly((v) => !v)}
            >
              Captioned only
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCcSelectAllVisible}
              disabled={!displayedPhotos.length}
            >
              {visibleAllSelected ? 'Deselect all' : 'Select all'}
            </Button>
          </div>
        </div>
        {ccLoadingFiles ? (
          <Spinner padded />
        ) : (
          <div className={classes.ccPhotoGrid}>
            {ccCaptionedOnly && displayedPhotos.length === 0 ? (
              <div className={classes.emptyState}>No captioned photos in this project.</div>
            ) : (
              displayedPhotos.map((file) => {
                const isSelected = ccSelectedPhotoKeys.has(file.key)
                return (
                  <button
                    key={file.key}
                    type="button"
                    className={`${classes.ccPhotoTile} ${isSelected ? classes.ccPhotoTileSelected : ''}`}
                    onClick={() => handleCcTogglePhoto(file)}
                  >
                    {file.src ? (
                      // CompanyCam photos are served from an external host that may
                      // not return CORS headers — keep these non-CORS (no crossOrigin)
                      // to match how provider files are displayed in the viewer.
                      <img src={file.src} alt={file.title ?? ''} className={classes.ccPhotoImg} />
                    ) : (
                      <span className={classes.tileFill}>
                        <FilePreview file={file} />
                      </span>
                    )}
                    {file.caption && (
                      // Show the CompanyCam description while selecting; clamped to
                      // 2 lines with the full text available on hover (#21212). The
                      // clamp lives on the inner span so the container's padding
                      // doesn't clip the last line on iOS Safari (#21231).
                      <span className={classes.ccPhotoCaption} title={file.caption}>
                        <span className={classes.ccPhotoCaptionText}>{file.caption}</span>
                      </span>
                    )}
                  </button>
                )
              })
            )}
            {ccHasMore && (
              <Button variant="outlined" onClick={handleCcLoadMore} fullWidth>
                Load more
              </Button>
            )}
          </div>
        )}
      </div>
    )
  }

  const showDealFilesConfirm = currentTab === 'deal-files' && selectedKeys.size > 0
  const showCcPickerFooter = currentTab === 'company-cam' && !!ccSelectedProject

  return (
    <Dialog {...dialogProps} maxWidth="md" fullWidth onClose={onCancel} classes={{ paper: classes.paper }}>
      <DialogTitle
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={onCancel} aria-label="Close">
            <CloseIcon />
          </IconButton>
        }
      >
        <div className={classes.titleStack}>
          <span>{title}</span>
          {subtitle && <span className={classes.titleSubtitle}>{subtitle}</span>}
        </div>
      </DialogTitle>
      <DialogContent classes={{ root: classes.content }}>
        <Tabs
          value={currentTab}
          onChange={(_, value) => setCurrentTab(value as AddFileDialogTab)}
          classes={{ root: classes.tabsRow }}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab value="upload" label={renderTabLabel('Upload')} />
          {dealFilesAvailable && <Tab value="deal-files" label={renderTabLabel(dealFilesLabel)} />}
          {companyCamAvailable && <Tab value="company-cam" label={renderTabLabel('Company Cam')} />}
        </Tabs>
        {currentTab === 'upload' && renderUploadPanel()}
        {currentTab === 'deal-files' && renderDealFilesPanel()}
        {currentTab === 'company-cam' && renderCompanyCamPanel()}
      </DialogContent>
      {showDealFilesConfirm && (
        <DialogActions
          leftButton={
            <Button variant="text" onClick={onCancel}>
              Cancel
            </Button>
          }
        >
          <Button variant="contained" onClick={handleConfirmSelection}>
            Add {selectedKeys.size} {selectedKeys.size === 1 ? 'file' : 'files'}
          </Button>
        </DialogActions>
      )}
      {showCcPickerFooter && (
        <DialogActions
          leftButton={
            <Button variant="text" onClick={handleCcBack}>
              Back
            </Button>
          }
        >
          <Button variant="contained" onClick={handleCcConfirm} disabled={ccSelectedPhotoKeys.size === 0}>
            Confirm{ccSelectedPhotoKeys.size > 0 ? ` (${ccSelectedPhotoKeys.size})` : ''}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default withUser(withClientOptions(AddFileDialog))
